import { CloseThin, SendAllToggleOff } from '@/resources/assets/images';
import classNames from 'classnames';
import Modali, { useModali } from 'modali';
import React, { ReactNode, ReactNodeArray, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import './Modal.scss';

import { emailRemoveSubjectForEmail } from '@/modules/email/emailActions';
import type { DirectEmail } from '@/modules/email/emailTypes';
import { sendAnalytics } from '@/services/analyticsService';
import type { RootState } from '@/rootReducer';
import { Tooltip } from '../atoms/Tooltip';

type Props = {
	title: ReactNode | string;
	show: boolean;
	closable?: boolean;
	size?: string;
	height?: string;
	overlayClose?: boolean;

	content: ReactNode | string;

	titleClass?: string;

	keyboardClose?: boolean;

	buttons: ReactNodeArray;

	bodyClass?: string;

	closeIcon?: React.ReactNode;

	onClose?: () => void;

	sendAll?: boolean;
	setShowSendAll?: (x: boolean) => void;
	backToTemplateEdit?: () => void;
	customEmailModalDisplay?: boolean;
	setCustomEmailModalDisplay?: (x: boolean) => void;
	sendAllMode?: boolean;
	setSendAllMode?: (x: boolean) => void;
	showTemplates?: boolean;
	directEmail?: DirectEmail;
};

export default function Modal({
	title,
	show = false,
	content,
	titleClass,
	buttons,
	onClose,
	keyboardClose = false,
	closable = true,
	size = 'md',
	height = 'auto',
	bodyClass,
	closeIcon,
	overlayClose = false,
	setShowSendAll,
	sendAllMode,
	setSendAllMode,
	backToTemplateEdit,
	customEmailModalDisplay,
	setCustomEmailModalDisplay,
	showTemplates,
	directEmail
}: Props): JSX.Element {
	const [display, setDisplay] = useState<boolean>(false);

	const [modal, toggleModal] = useModali({
		animated: true,
		closeButton: false,
		buttons,
		onHide: onClose,
		keyboardClose: false,
		overlayClose: false,
		onOverlayClicked: () => {
			if (setCustomEmailModalDisplay) {
				setCustomEmailModalDisplay(!overlayClose);
			} else {
				setDisplay(!overlayClose);
			}
		},
		onEscapeKeyDown: () => {
			if (setCustomEmailModalDisplay) {
				setCustomEmailModalDisplay(!keyboardClose);
			} else {
				setDisplay(!keyboardClose);
			}
		}
	});

	const [businessesLoading] = useSelector(
		(state: RootState) => [
			state.dashboard.getIn(['businesses', 'allDataLoading'])
		],
		shallowEqual
	);

	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	const dispatch = useDispatch();

	const [isMounted, setIsMounted] = useState<boolean>(false);

	const removeSubjectForEmail = () => dispatch(emailRemoveSubjectForEmail());

	useEffect(() => {
		setIsMounted(true);
	}, []);

	useEffect(() => {
		if (show) {
			toggleModal();

			if (setCustomEmailModalDisplay) {
				setCustomEmailModalDisplay(true);
			} else {
				setDisplay(true);
			}
		} else if (setCustomEmailModalDisplay) {
			setCustomEmailModalDisplay(false);
		} else {
			setDisplay(false);
		}
	}, [show]);

	useEffect(() => {
		if (isMounted && (!display || !customEmailModalDisplay)) {
			if (document.getElementsByClassName('modali-animated')[0]) {
				document
					.getElementsByClassName('modali-animated')[0]
					.classList.add('modali-animation-fade-out');

				setTimeout(toggleModal, 300);
			}
		}
	}, [display, customEmailModalDisplay]);

	const unsetReplySubject = () => {
		setTimeout(() => {
			removeSubjectForEmail();
		}, 1000);
	};

	return (
		// eslint-disable-next-line react/jsx-props-no-spreading
		<Modali.Modal {...modal} class="bodyClass">
			<div
				className={classNames(
					'modal',
					{ [`modal-${size}`]: size },
					'flex',
					'flex-col',
					bodyClass,
					{ [`modal-h-${height}`]: height }
				)}
			>
				<div className="flex items-center justify-between">
					<div className="modal-title w-full">
						<div
							className={classNames(
								'font-light',
								{
									'text-3xl text-white': !titleClass
								},
								titleClass,
								title
							)}
						>
							{title}
						</div>
					</div>
					{!showTemplates &&
						!directEmail &&
						titleClass === 'emailModal-title' && (
							<Tooltip.Provider>
								<Tooltip.Root>
									<Tooltip.Trigger asChild>
										<span
											data-is-disabled={businessesLoading}
											className='send-all-toggle-classic data-[is-disabled="true"]:cursor-not-allowed data-[is-disabled="true"]:opacity-60'
										>
											Try Send All Mode
											<button
												type="button"
												disabled={businessesLoading}
												onClick={() => {
													if (setShowSendAll) {
														setShowSendAll(true);
													}
													if (setSendAllMode) {
														setSendAllMode(
															!sendAllMode
														);
													}
													if (backToTemplateEdit) {
														backToTemplateEdit();
													}
													const el =
														document.querySelector(
															'.modali'
														);
													el?.classList.add('hide');
													sendAnalytics({
														type: 'track',
														userId: user.email,
														eventName:
															'user_clicked_try_send_all_mode'
													});
												}}
											>
												<SendAllToggleOff className="send-all-toggle-icon-off" />
											</button>
										</span>
									</Tooltip.Trigger>

									<Tooltip.Content
										className="z-[9999]"
										disabled={!businessesLoading}
									>
										Loading your business… Please wait a
										moment.{' '}
									</Tooltip.Content>
								</Tooltip.Root>
							</Tooltip.Provider>
						)}
					{closable && (
						<button
							className="modal-close-button flex h-full"
							type="button"
							onClick={() => {
								if (setCustomEmailModalDisplay) {
									setCustomEmailModalDisplay(false);
								} else {
									setDisplay(false);
								}

								unsetReplySubject();
							}}
						>
							{closeIcon ?? (
								<CloseThin
									width={18}
									height={18}
									className="text-white"
								/>
							)}
						</button>
					)}
				</div>
				<div className="relative h-full">{content}</div>
			</div>
		</Modali.Modal>
	);
}
